/**
 * Cómo se ve cada fuente del Brain en el front: ícono de lucide por clave y tono
 * por clases. La lista de fuentes y syncs vive en shared/brain-sources.ts (sin
 * React); acá solo se mapea. Los Records tipados por `BrainSourceId` /
 * `BrainSyncId` hacen que una fuente nueva sin etiqueta no compile.
 */
import { BarChart2, Briefcase, FileText, Heart, Mail, MessageCircle, MessageSquare, Sparkles, Star, Video, type LucideIcon } from 'lucide-react';
import {
  BRAIN_SOURCES,
  type BrainSourceIcon,
  type BrainSourceId,
  type BrainSourceTone,
  type BrainSyncId,
} from '../../../../shared/brain-sources';

export const BRAIN_SOURCE_ICONS: Record<BrainSourceIcon, LucideIcon> = {
  'message-square': MessageSquare,
  video: Video,
  'bar-chart': BarChart2,
  star: Star,
  'file-text': FileText,
  sparkles: Sparkles,
  heart: Heart,
  briefcase: Briefcase,
  mail: Mail,
  'message-circle': MessageCircle,
};

export const BRAIN_SOURCE_TONES: Record<BrainSourceTone, { color: string; bg: string; border: string }> = {
  slack:   { color: 'text-teal-600',   bg: 'bg-teal-500/10',   border: 'border-teal-500/20' },
  meet:    { color: 'text-purple-600', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
  revenue: { color: 'text-blue-600',   bg: 'bg-blue-500/10',   border: 'border-blue-500/20' },
  nps:     { color: 'text-yellow-600', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
  docs:    { color: 'text-gray-600',   bg: 'bg-gray-500/10',   border: 'border-gray-500/20' },
  derived: { color: 'text-rose-600',   bg: 'bg-rose-500/10',   border: 'border-rose-500/20' },
  crm:     { color: 'text-orange-600', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
  gmail:   { color: 'text-red-600',    bg: 'bg-red-500/10',    border: 'border-red-500/20' },
  gchat:   { color: 'text-green-600',  bg: 'bg-green-500/10',  border: 'border-green-500/20' },
};

/** Claves i18n literales (el guard-rail de i18n las ve) — una por fuente y por sync. */
export const BRAIN_SOURCE_LABEL_KEYS: Record<BrainSourceId, string> = {
  slack: 'brain.source.slack',
  meet_transcript: 'brain.source.meet_transcript',
  bigquery: 'brain.source.bigquery',
  nps: 'brain.source.nps',
  manual: 'brain.source.manual',
  brain_extension: 'brain.source.brain_extension',
  highlights: 'brain.source.highlights',
  sentiment: 'brain.source.sentiment',
  hubspot: 'brain.source.hubspot',
  gmail: 'brain.source.gmail',
  google_chat: 'brain.source.google_chat',
};

export const BRAIN_SYNC_LABEL_KEYS: Record<BrainSyncId, string> = {
  slack: 'brain.sync.slack',
  bq: 'brain.sync.bq',
  meet: 'brain.sync.meet',
  markdown: 'brain.sync.markdown',
  hubspot: 'brain.sync.hubspot',
  gmail: 'brain.sync.gmail',
  gchat: 'brain.sync.gchat',
  calendar: 'brain.sync.calendar',
};

export function brainSourceUi(source: BrainSourceId): { Icon: LucideIcon; color: string; bg: string; border: string } {
  const def = BRAIN_SOURCES[source];
  return { Icon: BRAIN_SOURCE_ICONS[def.icon], ...BRAIN_SOURCE_TONES[def.tone] };
}
